import { useEffect, useMemo, useState, type ChangeEvent } from "react";

interface ForumCategory {
  id: string;
  name: string;
  color: string;
}

export interface ForumComposerPayload {
  category: string;
  title: string;
  content?: string;
  imageFile: File | null;
}

interface ForumComposerModalProps {
  categories: ForumCategory[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: (payload: ForumComposerPayload) => void;
}

export default function ForumComposerModal({
  categories,
  submitting,
  onClose,
  onSubmit
}: ForumComposerModalProps) {
  const [category, setCategory] = useState(categories[0]?.id ?? "general");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);

  const imagePreview = useMemo(() => (imageFile ? URL.createObjectURL(imageFile) : null), [imageFile]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setImageFile(file);
  };

  const handleSubmit = () => {
    onSubmit({
      category,
      title: title.trim(),
      content: content.trim() || undefined,
      imageFile
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl rounded-[28px] bg-white border border-gray-100 shadow-[0_30px_100px_rgba(0,0,0,0.16)] overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#1A1A1A]">Nouvelle publication forum</h3>
            <p className="text-sm text-gray-500 mt-1">Partage une information, une idée ou une image avec toute la communauté connectée.</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl hover:bg-gray-100 transition-colors flex items-center justify-center text-gray-500"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Catégorie</label>
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
              >
                {categories.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Titre</label>
              <input
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Ex: Tournoi inter-entreprises ASC"
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                minLength={3}
                maxLength={120}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Contenu</label>
            <textarea
              value={content}
              onChange={(event) => setContent(event.target.value)}
              placeholder="Décris ton sujet, ton annonce ou ton retour d'expérience..."
              className="w-full min-h-[180px] rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors resize-none"
              minLength={10}
              maxLength={4000}
            />
            <div className="mt-2 text-right text-xs text-gray-400">{content.length}/4000</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Image facultative</label>
            <label className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-4 text-sm text-gray-500 cursor-pointer hover:border-[#E8521A] hover:text-[#E8521A] transition-colors">
              <i className="ri-image-add-line text-lg"></i>
              Ajouter une image
              <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
            </label>
          </div>

          {imagePreview ? (
            <div className="relative rounded-2xl overflow-hidden border border-gray-100">
              <img src={imagePreview} alt="Aperçu" className="w-full h-64 object-cover" />
              <button
                onClick={() => setImageFile(null)}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/55 text-white flex items-center justify-center"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          ) : null}
        </div>

        <div className="px-6 py-5 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !title.trim() || (content.trim().length < 10 && !imageFile)}
            className="px-5 py-2.5 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? "Publication..." : "Publier"}
          </button>
        </div>
      </div>
    </div>
  );
}
